/**
 * VENDORS — wire format, shaping and formatting
 * =============================================
 * One place that knows what the admin API sends, so neither screen has to.
 *
 * The vendors API is being reshaped in parallel (server-side q / status /
 * sort / dir, `listings_count`, `last_activity`, `approval_id` on a listing).
 * Everything below therefore reads BOTH the shape that is shipping today and
 * the shape in the contract, because a UI that goes blank the moment a field
 * is renamed is a UI nobody trusts. Where the two disagree the contract wins
 * and the legacy field is the fallback — never the other way round.
 */

const API = process.env.REACT_APP_SERVER_URL;

/* ────────────────────────────────────────────────────────────────────────
   transport
   ──────────────────────────────────────────────────────────────────────── */

/**
 * This API answers "you are not signed in" with HTTP 201 and an empty
 * string for `data`, so `res.ok` proves nothing. Read the envelope.
 */
async function unwrap(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    throw new Error("The server sent a response this screen could not read.");
  }
  if (body?.success === false || body?.status === 201 || body?.status === 202) {
    throw new Error(body?.msg || "The request was refused.");
  }
  if (!res.ok) throw new Error(body?.msg || `Request failed (${res.status}).`);
  return body?.data;
}

export const get = (path, signal) =>
  fetch(`${API}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  }).then(unwrap);

/**
 * Same request, but keeps the `meta` block — `{q,status,sort,dir,limit,
 * offset,count}`. `count` is the true size of the result set, which the
 * returned array is not once `limit` bites.
 */
export const getPage = (path, signal) =>
  fetch(`${API}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  }).then(async (res) => {
    let body = null;
    try {
      body = await res.json();
    } catch {
      throw new Error("The server sent a response this screen could not read.");
    }
    if (body?.success === false || body?.status === 201 || body?.status === 202) {
      throw new Error(body?.msg || "The request was refused.");
    }
    if (!res.ok) throw new Error(body?.msg || `Request failed (${res.status}).`);
    return { data: body?.data, meta: body?.meta || null };
  });

export const post = (path, payload) =>
  fetch(`${API}${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  }).then(unwrap);

/* ────────────────────────────────────────────────────────────────────────
   verification vocabulary
   ──────────────────────────────────────────────────────────────────────── */

/** Workflow order, which is what "sort by status" should mean to an
 *  operator: the ones needing work first, the settled ones last. */
export const STATUS_ORDER = {
  unverified: 0,
  pending: 1,
  verified: 2,
  rejected: 3,
};

export const STATUS_LABEL = {
  unverified: "Unverified",
  pending: "In review",
  verified: "Verified",
  rejected: "Rejected",
};

/** Maps onto the shared severity ramp in opsConsole.module.css. */
export const STATUS_TONE = {
  unverified: "muted",
  pending: "warn",
  verified: "ok",
  rejected: "danger",
};

export const STATUS_KEYS = ["unverified", "pending", "verified", "rejected"];

export const normStatus = (s) =>
  STATUS_LABEL[s] ? s : "unverified";

/* ────────────────────────────────────────────────────────────────────────
   row shaping
   ──────────────────────────────────────────────────────────────────────── */

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

const firstOf = (...vals) => vals.find((v) => v !== undefined && v !== null);

/** A directory row, whichever generation of the API produced it. */
export function shapeVendor(raw) {
  const listings = raw?.listings && typeof raw.listings === "object" ? raw.listings : {};
  const approvals = raw?.approvals && typeof raw.approvals === "object" ? raw.approvals : {};
  const total = firstOf(raw?.listings_count, listings.total, 0);
  return {
    id: raw?.id,
    name: raw?.name || raw?.company || "",
    company: raw?.company || "",
    owner_email: raw?.owner_email || "",
    gstin: raw?.gstin || "",
    verification_status: normStatus(raw?.verification_status),
    margin_rate: num(raw?.margin_rate),
    margin_flat_cut: num(raw?.margin_flat_cut),
    listings_count: Number(total) || 0,
    listings_live: Number(listings.active || 0),
    listings_draft: Number(listings.draft || 0),
    open_reviews: Number(firstOf(raw?.open_approvals, approvals.open, 0)) || 0,
    created_at: firstOf(raw?.created_at, raw?.createdAt, null),
    // Not every generation of the API sends this. Falling back to "joined"
    // would be a lie — a vendor who has never touched anything has no
    // activity, and the column must be allowed to say so.
    last_activity: firstOf(raw?.last_activity, raw?.updated_at, raw?.updatedAt, null),
  };
}

/**
 * A listing row on the detail page.
 *
 * Three distinct states, and conflating any two of them is the bug the
 * "clicking a listing should open its approval" request was really about:
 *
 *   has_open_approval        → there is a decision waiting on us
 *   approval_id, but closed  → there is a decision already made, worth reading
 *   approval_id === null     → nothing was ever submitted; there is no page to
 *                              open and the row must not pretend otherwise
 *
 * `approval_id` alone does NOT mean "open" — the API falls back to the newest
 * CLOSED approval so decision history stays reachable. Gate the live-review
 * affordance on has_open_approval.
 *
 * `approval_id: undefined` (as opposed to null) means an older build of the
 * API that never sent the field; only then is it worth deriving one from the
 * submission history.
 */
export function shapeListing(raw) {
  const hasField = raw && "approval_id" in raw;
  return {
    id: raw?.id,
    name: raw?.name || `SKU ${raw?.id}`,
    status: raw?.status || "",
    updated_at: firstOf(raw?.updated_at, raw?.updatedAt, raw?.createdAt, null),
    approval_id: hasField ? (raw.approval_id ?? null) : undefined,
    approval_status: raw?.approval_status || "",
    has_open_approval: Boolean(raw?.has_open_approval),
  };
}

/** Approval states that still want a human. */
export const OPEN_APPROVAL = new Set(["reviewing", "changes_requested"]);

/* ────────────────────────────────────────────────────────────────────────
   the pipe-joined columns
   ──────────────────────────────────────────────────────────────────────── */

/**
 * `vendors.tax_id` is "gst|taxId|cin|pan" and `vendors.bank_deatils` is
 * "holder|account|bank|ifsc|swift" (the column really is misspelled). The
 * API splits both, but an all-empty record comes back as four empty strings
 * rather than an absent object — which is exactly the case that used to
 * render as a wall of em-dashes and read as a broken page.
 *
 * `blanks()` answers "is this section genuinely unfilled" so the UI can say
 * so in words.
 */
export const blanks = (obj, keys) =>
  keys.every((k) => !String(obj?.[k] ?? "").trim());

export const val = (v) => {
  const s = String(v ?? "").trim();
  return s === "" || s === "null" || s === "undefined" ? "" : s;
};

/* ────────────────────────────────────────────────────────────────────────
   formatting
   ──────────────────────────────────────────────────────────────────────── */

export const date = (d) => {
  if (!d) return "";
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** "3d ago" — the thing an operator actually reads on an activity column. */
export const ago = (d) => {
  if (!d) return "";
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 31) return `${days}d ago`;
  const mons = Math.round(days / 30.4);
  if (mons < 12) return `${mons}mo ago`;
  return `${Math.round(mons / 12)}y ago`;
};

/** margin_rate is stored as a fraction: 0.125 → "12.5%". */
export const pct = (n) => {
  if (n === null || n === undefined) return "";
  const v = Number(n) * 100;
  return `${v % 1 ? v.toFixed(1) : v.toFixed(0)}%`;
};

export const money = (n) =>
  n === null || n === undefined ? "" : `₹${Number(n).toLocaleString("en-IN")}`;

/* ────────────────────────────────────────────────────────────────────────
   sorting
   ──────────────────────────────────────────────────────────────────────── */

const ts = (d) => {
  const t = new Date(d || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Server-side sorting is authoritative — every request carries ?sort=&dir=.
 * These comparators run over the result anyway so that the arrow in the
 * header is never lying: if a param is ignored, dropped or not implemented
 * yet, the table still shows what it says it is showing. When the server
 * does honour the param the two orders agree and this is a no-op.
 */
export const VENDOR_SORTS = {
  name: (r) => (r.name || r.company || "").toLowerCase(),
  gstin: (r) => r.gstin.toLowerCase(),
  status: (r) => STATUS_ORDER[r.verification_status] ?? 99,
  listings: (r) => r.listings_count,
  joined: (r) => ts(r.created_at),
  last_activity: (r) => (r.last_activity ? ts(r.last_activity) : null),
};

export const LISTING_SORTS = {
  name: (r) => r.name.toLowerCase(),
  status: (r) => r.status.toLowerCase(),
  updated: (r) => ts(r.updated_at),
};

const isBlank = (v) => v === null || v === undefined || v === "";

/** Blanks always sink to the bottom, in both directions — an empty GSTIN is
 *  missing data, not the alphabetically-first GSTIN. */
export function sortRows(rows, sorters, key, dir) {
  const pick = sorters[key];
  if (!pick) return rows;
  const sign = dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = pick(a);
    const vb = pick(b);
    const ba = isBlank(va);
    const bb = isBlank(vb);
    if (ba || bb) return ba && bb ? 0 : ba ? 1 : -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}
